import { readStringList } from "../content/contentDecoders";
import { readArray, readInteger, readObject, readString, type JsonObject } from "../http/jsonDecoding";
import type { AnnotationSet } from "../threads/liveMessages";
import { readExtractionSummary, readInstanceStatement, type ExtractionSummary, type InstanceStatement } from "./knowledgeDataDecoders";

/**
 * The two lenses on the knowledge data (F84 §9), as the server sends them: what was recorded
 * on a document (`DocumentResultBody`) and the results that use a term (`TermUseBody`).
 */

/** A result on a document; an annotation result carries its spans as the Thread showed them. */
export interface DocumentResult extends ExtractionSummary {
  /** For an annotation result: the set, ready for `AnnotationSetView`; `null` for instance data. */
  readonly annotationSet: AnnotationSet | null;
}

/** An instance-data result that uses a term. */
export interface TermUse extends ExtractionSummary {
  /** Its individuals typed with the term (as a class). */
  readonly individuals: number;
  /** Its triples with the term as predicate (as a property). */
  readonly termStatements: number;
}

export function readDocumentResult(value: unknown): DocumentResult {
  const summary = readExtractionSummary(value);
  const body = readObject(value, "document result");
  const documents = readStringList(body, "documents");
  return {
    ...summary,
    annotationSet: summary.kind === "annotations" && summary.labelList !== null
      ? {
          output_kind: "annotations",
          document: summary.document,
          ...(documents.length === 0 ? {} : { documents }),
          label_list: summary.labelList,
          annotations: readAnnotations(body),
        }
      : null,
  };
}

/** An accepted instance-data result on one exact document version, with the statements said on it. */
export interface AcceptedResult {
  /** The result key, which opens the result's own page. */
  readonly source: string;
  /** The model version the data was written against (`<collection>@<commit>`). */
  readonly model: string;
  readonly statements: readonly InstanceStatement[];
}

export function readAcceptedResult(value: unknown): AcceptedResult {
  const body = readObject(value, "accepted result");
  return {
    source: readString(body, "source"),
    model: readString(body, "model"),
    statements: readArray(body["statements"], "statements", readInstanceStatement),
  };
}

export function readTermUse(value: unknown): TermUse {
  const body = readObject(value, "term use");
  return { ...readExtractionSummary(value), individuals: readInteger(body, "individuals"), termStatements: readInteger(body, "term_statements") };
}

/** The spans of an annotation set, as `dmw-annotation-set/1` and module D both write them. */
export function readAnnotations(body: JsonObject): AnnotationSet["annotations"] {
  return readArray(body["annotations"], "annotations", (item) => {
    const span = readObject(item, "annotation");
    return {
      segment: readString(span, "segment"),
      start: readInteger(span, "start"),
      end: readInteger(span, "end"),
      quote: readString(span, "quote"),
      label: readString(span, "label"),
      note: readString(span, "note"),
    };
  });
}
